import { useSyncExternalStore } from 'react';
import type { EventStatus, Permission } from '@barso/shared';
import type { AreaFeatures } from './areas';

/**
 * Kontext der aktuellen Seite für Kopfzeile und Brotkrumen: welcher Basar (und welche Organisation) gerade
 * geöffnet ist. Die Basar-Hülle (`EventShell`) setzt ihn, sobald der Basar geladen ist, und räumt ihn beim
 * Verlassen wieder auf. Fehlt er (z. B. in Einzeltests oder vor dem Laden), nutzen die Brotkrumen nur die
 * Angaben der Seite selbst.
 */
export interface NavContext {
  eventId: string | null;
  eventTitle: string | null;
  orgId: string | null;
  orgName: string | null;
  /** Hat die Person in diesem Basar das Recht zu kassieren (für den Kasse-Reiter am Handy)? */
  canSell?: boolean;
  /** Status und Rechte der Person im geöffneten Basar (für „⋯ Mehr“ der App, nur Anzeige). */
  status?: EventStatus;
  permissions?: readonly Permission[];
  /** Schalter der Zusatzfunktionen des Basars (F0): Bereiche einer ausgeschalteten Funktion fehlen im Blatt. */
  features?: AreaFeatures | undefined;
}

const EMPTY: NavContext = {
  eventId: null,
  eventTitle: null,
  orgId: null,
  orgName: null,
  canSell: false,
};

let current: NavContext = EMPTY;
/** Zuletzt geöffneter Basar (nur Arbeitsspeicher): bleibt nach dem Verlassen des Basars stehen, bis zur Abmeldung. */
let last: NavContext | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export const navContextStore = {
  get: (): NavContext => current,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  set: (next: NavContext): void => {
    if (
      next.eventId === current.eventId &&
      next.eventTitle === current.eventTitle &&
      next.orgId === current.orgId &&
      next.orgName === current.orgName &&
      next.canSell === current.canSell &&
      next.status === current.status &&
      (next.permissions ?? []).join(',') === (current.permissions ?? []).join(',') &&
      JSON.stringify(next.features ?? {}) === JSON.stringify(current.features ?? {})
    ) {
      return;
    }
    current = next;
    if (next.eventId !== null) last = next;
    emit();
  },
  /** Zuletzt geöffneter Basar, auch wenn gerade keiner offen ist (für „Mehr“ und Einstellungen der App). */
  getLast: (): NavContext | null => last,
  /** Vergisst auch den zuletzt geöffneten Basar (Abmelden, Nutzerwechsel). */
  reset: (): void => {
    last = null;
    current = EMPTY;
    emit();
  },
  clear: (): void => {
    if (current === EMPTY) return;
    current = EMPTY;
    emit();
  },
};

export function useNavContext(): NavContext {
  return useSyncExternalStore(navContextStore.subscribe, navContextStore.get);
}

/** Geöffneter Basar, sonst der zuletzt geöffnete (nur Anzeige: Rechte prüft weiter der Server). */
export function useNavContextOrLast(): NavContext {
  const now = useNavContext();
  return now.eventId !== null ? now : (navContextStore.getLast() ?? now);
}
